import * as React from 'react';
import { X, Package, Plus, DollarSign, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SearchPriceInput } from './search-price-input/index';
import { usePrice } from '@/lib/api/prices';
import { useProduct } from '@/lib/api/products';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from "@/lib/utils";
import { getCurrencyOption } from '@/lib/currency/currencyOptionsKeys';
import { formatMoney } from "@/lib/money";

export interface PriceLineItem {
  productId: string;
  productName: string;
  variantId: string;
  variantName: string;
  imageUrl: string | null;
  priceId: string;
  quantity: number;
}

interface PriceLineItemsSelectorProps {
  storeId: string;
  lineItems: PriceLineItem[];
  onLineItemsChange: (lineItems: PriceLineItem[]) => void;
  error?: string;
  label?: string;
  required?: boolean;
  /** Also list inactive products in the picker (checkout-to-transact surfaces
   *  only — see SearchPriceInputProps, TAG-1595). */
  includeInactive?: boolean;
}

const formatPrice = (currencyOptions: Record<string, { amount: number }> | undefined, currency = 'USD') => {
  if (!currencyOptions || Object.keys(currencyOptions).length === 0) {
    return formatMoney(0, currency);
  }

  const resolved = getCurrencyOption(currencyOptions, currency);
  if (!resolved) return formatMoney(0, currency);

  return formatMoney(resolved.option.amount, resolved.currency);
};

const formatBillingInterval = (billingInterval: string | null, count: number | null, t: (key: string, opts?: Record<string, unknown>) => string) => {
  if (!billingInterval || billingInterval === 'one_time') return t('stores:billingOneTime');
  const intervalText = count && count > 1 ? `${count} ${billingInterval}s` : billingInterval;
  return t('stores:billingEvery', { interval: intervalText });
};

const LineItemDisplay = ({
  item,
  index,
  storeId,
  isEditing,
  includeInactive = false,
  onQuantityChange,
  onRemove,
  onEdit,
  onCancelEdit,
  onPriceChange,
}: {
  item: PriceLineItem;
  index: number;
  storeId: string;
  isEditing: boolean;
  includeInactive?: boolean;
  onQuantityChange: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onCancelEdit: () => void;
  onPriceChange: (selection: { priceId: string; productId: string; variantId: string }) => void;
}) => {
  const { t } = useTranslation(['stores', 'common']);
  const { data: price } = usePrice(item.priceId, true);

  if (isEditing) {
    return (
      <div className={cn('bg-card space-y-3 overflow-visible rounded-md border p-4')}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">
            {t('stores:editingPrice')}
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={onCancelEdit} className="h-8">
            {t('common:cancel')}
          </Button>
        </div>
        <SearchPriceInput storeId={storeId} priceId={item.priceId} onChange={onPriceChange} includeInactive={includeInactive} />
      </div>
    );
  }

  return (
    <div className={cn('bg-card flex items-start gap-3 rounded-md border p-4')}>
      <div className="bg-muted flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
        ) : (
          <Package className="text-muted-foreground h-6 w-6" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div>
          <p className="truncate text-sm font-medium">{item.productName}</p>
          <p className="text-muted-foreground truncate text-xs">{item.variantName}</p>
          {price && (
            <div className="mt-1 flex items-center gap-2">
              <DollarSign className="text-muted-foreground h-3 w-3" />
              <p className="text-muted-foreground text-xs">
                {formatPrice(price.currencyOptions)} -{' '}
                {formatBillingInterval(price.billingInterval ?? null, price.billingIntervalCount ?? null, t)}
              </p>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor={`quantity-${index}`} className="text-xs">
            {t('stores:quantity')}
          </Label>
          <Input
            id={`quantity-${index}`}
            type="number"
            min="1"
            value={item.quantity}
            onChange={(e) => onQuantityChange(index, parseInt(e.target.value) || 1)}
            className="h-9 max-w-[120px] text-sm"
          />
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-col gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onEdit(index)}
          className="h-8 w-8 p-0"
          title={t('common:edit')}
        >
          <Pencil className="h-4 w-4" />
          <span className="sr-only">{t('common:edit')}</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="h-8 w-8 p-0"
          title={t('common:remove')}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common:remove')}</span>
        </Button>
      </div>
    </div>
  );
};

export const PriceLineItemsSelector = ({
  storeId,
  lineItems,
  onLineItemsChange,
  error,
  label,
  required = false,
  includeInactive = false,
}: PriceLineItemsSelectorProps) => {
  const { t } = useTranslation(['stores', 'common']);
  const [showSearch, setShowSearch] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handlePriceSelect = (selection: { priceId: string; productId: string; variantId: string }) => {
    // Check if this price is already in the list
    const exists = lineItems.some((item) => item.priceId === selection.priceId);
    if (exists) {
      return;
    }

    // Add new item
    const newLineItem: PriceLineItem = {
      productId: selection.productId,
      productName: '', // Will be filled by PriceItemLoader
      variantId: selection.variantId,
      variantName: '', // Will be filled by PriceItemLoader
      imageUrl: null, // Will be filled by PriceItemLoader
      priceId: selection.priceId,
      quantity: 1,
    };

    onLineItemsChange([...lineItems, newLineItem]);
    setShowSearch(false);
  };

  const handlePriceEdit = (selection: { priceId: string; productId: string; variantId: string }) => {
    if (editingIndex === null) return;

    // Update existing item
    const updatedItems = [...lineItems];
    updatedItems[editingIndex] = {
      ...updatedItems[editingIndex],
      productId: selection.productId,
      productName: '', // Will be filled by PriceItemLoader
      variantId: selection.variantId,
      variantName: '', // Will be filled by PriceItemLoader
      imageUrl: null, // Will be filled by PriceItemLoader
      priceId: selection.priceId,
    };
    onLineItemsChange(updatedItems);
    setEditingIndex(null);
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = { ...updatedItems[index], quantity: Math.max(1, quantity) };
    onLineItemsChange(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = lineItems.filter((_, i) => i !== index);
    onLineItemsChange(updatedItems);
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {label && (
          <Label className="text-sm font-medium">
            {label} {required && '*'}
          </Label>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => setShowSearch(!showSearch)}>
          <Plus className="mr-1 h-4 w-4" />
          {t('stores:addProduct')}
        </Button>
      </div>

      {showSearch && (
        <div className="bg-muted/30 space-y-2 rounded-lg border p-4">
          <SearchPriceInput storeId={storeId} onChange={handlePriceSelect} includeInactive={includeInactive} />
          <p className="text-muted-foreground text-xs">
            {t('stores:searchPriceToAdd')}
          </p>
        </div>
      )}

      {error && <p className="text-xs leading-tight text-red-600">{error}</p>}

      {lineItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm">
            {t('stores:selectedProducts')} ({lineItems.length})
          </p>
          <div className="max-h-[500px] space-y-2 overflow-y-auto px-1 -mx-1">
            {lineItems.map((item, index) => (
              <PriceItemLoader
                key={`${item.priceId}-${index}`}
                item={item}
                index={index}
                storeId={storeId}
                isEditing={editingIndex === index}
                includeInactive={includeInactive}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveItem}
                onEdit={handleEditItem}
                onCancelEdit={handleCancelEdit}
                onPriceChange={handlePriceEdit}
                onUpdate={(updatedItem) => {
                  const updatedItems = [...lineItems];
                  updatedItems[index] = updatedItem;
                  onLineItemsChange(updatedItems);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {lineItems.length === 0 && (
        <div className="bg-muted/30 rounded-lg border py-12 text-center">
          <Package className="text-muted-foreground mx-auto mb-3 h-12 w-12" />
          <p className="text-muted-foreground mb-1 text-sm font-medium">
            {t('stores:noProductsSelected')}
          </p>
          <p className="text-muted-foreground text-xs">
            {t('stores:clickAddProduct')}
          </p>
        </div>
      )}
    </div>
  );
};

const PriceItemLoader = ({
  item,
  index,
  storeId,
  isEditing,
  includeInactive = false,
  onQuantityChange,
  onRemove,
  onEdit,
  onCancelEdit,
  onPriceChange,
  onUpdate,
}: {
  item: PriceLineItem;
  index: number;
  storeId: string;
  isEditing: boolean;
  includeInactive?: boolean;
  onQuantityChange: (index: number, quantity: number) => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onCancelEdit: () => void;
  onPriceChange: (selection: { priceId: string; productId: string; variantId: string }) => void;
  onUpdate: (item: PriceLineItem) => void;
}) => {
  const { data: price } = usePrice(item.priceId, true);
  const { data: product } = useProduct(item.productId, true);

  React.useEffect(() => {
    if (price && product && (!item.productName || !item.variantName)) {
      const variant = product.variants.find((v: any) => v.id === item.variantId);
      if (variant) {
        onUpdate({
          ...item,
          productName: product.name,
          variantName: variant.name,
          imageUrl: variant.imageUrl ?? product.variants.find((v: any) => v.default)?.imageUrl ?? null,
        });
      }
    }
  }, [price, product, item, onUpdate]);

  if (!item.productName || !item.variantName) {
    return (
      <div className="bg-card flex items-start gap-3 rounded-md border p-4">
        <Skeleton className="h-12 w-12 flex-shrink-0 rounded" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-40" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-9 w-[120px]" />
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col gap-1">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
    );
  }

  return (
    <LineItemDisplay
      item={item}
      index={index}
      storeId={storeId}
      isEditing={isEditing}
      includeInactive={includeInactive}
      onQuantityChange={onQuantityChange}
      onRemove={onRemove}
      onEdit={onEdit}
      onCancelEdit={onCancelEdit}
      onPriceChange={onPriceChange}
    />
  );
};
